import React from 'react';
import { DashboardLayout } from './DashboardLayout';
import { DesignerSteps, STEP_NAMES, useDesignerSteps } from '../designer/DesignerSteps';
import { motion } from 'framer-motion';
import { SparklesIcon, ArrowLeftIcon, CheckCircle2Icon, CircleIcon } from 'lucide-react';
export function DashboardDesignerPage() {
  const {
    step,
    data,
    updateData,
    nextStep,
    prevStep,
    setStep
  } = useDesignerSteps();
  const progress = step / 5 * 100;
  return <DashboardLayout pageTitle="AI Designer" breadcrumb={`Step ${Math.min(step, 5)} of 5 · ${STEP_NAMES[step - 1]}`} data-id="element-1130">
      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6" data-id="element-1131">
        {/* Step rail */}
        <aside className="xl:sticky xl:top-24 self-start" data-id="element-1132">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-id="element-1133">
            <div className="flex items-center gap-2 mb-4" data-id="element-1134">
              <div className="w-9 h-9 rounded-lg bg-brand-coral/10 text-brand-coral flex items-center justify-center" data-id="element-1135">
                <SparklesIcon className="w-4 h-4" data-id="element-1136" />
              </div>
              <div data-id="element-1137">
                <div className="text-[10px] font-bold uppercase tracking-wider text-brand-coral" data-id="element-1138">
                  AI Designer
                </div>
                <div className="text-sm font-bold text-brand-navy leading-tight" data-id="element-1139">
                  Build your blueprint
                </div>
              </div>
            </div>

            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5" data-id="element-1140">
              <motion.div className="h-full bg-gradient-to-r from-brand-teal to-brand-coral" animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.4
            }} data-id="element-1141" />
            </div>

            <ol className="space-y-1.5" data-id="element-1142">
              {STEP_NAMES.map((name, i) => {
              const idx = i + 1;
              const done = idx < step;
              const active = idx === step;
              return <li key={name} data-id="element-1143">
                    <button type="button" onClick={() => {
                  if (idx <= step) setStep(idx);
                }} disabled={idx > step} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${active ? 'bg-brand-navy/5 text-brand-navy font-bold' : done ? 'text-slate-700 hover:bg-slate-50 cursor-pointer' : 'text-slate-400 cursor-not-allowed'}`} data-id="element-1144">
                      {done ? <CheckCircle2Icon className="w-4 h-4 text-brand-teal shrink-0" data-id="element-1145" /> : active ? <span className="relative shrink-0" data-id="element-1146">
                          <span className="w-4 h-4 rounded-full bg-brand-coral block" data-id="element-1147" />
                          <span className="absolute inset-0 rounded-full bg-brand-coral animate-ping opacity-40" data-id="element-1148" />
                        </span> : <CircleIcon className="w-4 h-4 shrink-0" data-id="element-1149" />}
                      <span className="text-[10px] font-mono text-slate-400 w-4 shrink-0" data-id="element-1150">
                        0{idx}
                      </span>
                      <span className="truncate" data-id="element-1151">{name}</span>
                    </button>
                  </li>;
            })}
            </ol>

            {step > 1 && step <= 5 && <button type="button" onClick={prevStep} className="mt-5 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold text-slate-500 hover:text-brand-navy px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-1152">
                <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-1153" />
                Back a step
              </button>}
          </div>

          <div className="hidden xl:block mt-4 bg-gradient-to-br from-brand-navy to-brand-navy/90 rounded-2xl p-5 text-white" data-id="element-1154">
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal mb-2" data-id="element-1155">
              Why we ask
            </div>
            <p className="text-xs text-slate-300 leading-relaxed" data-id="element-1156">
              Every question sharpens the AI blueprint. Skip is always available
              — we'll fill the rest from defaults.
            </p>
          </div>
        </aside>

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 min-h-[600px]" data-id="element-1157">
          <DesignerSteps step={step} data={data} updateData={updateData} nextStep={nextStep} prevStep={prevStep} data-id="element-1158" />
        </div>
      </div>
    </DashboardLayout>;
}